/* RESPONSIBLE TEAM: team-ai-agent */

/**
 * Shared utilities for AI Agent Guidance feature.
 *
 * These constants and utilities are used across the guidance composer,
 * guideline card, and guideline model for consistent character counting
 * and validation.
 */

/**
 * Maximum number of visible characters allowed in guidance text.
 * This is what users see and type - the actual content excluding HTML markup.
 */
export const MAX_VISIBLE_CHARACTER_COUNT = 2500;

/**
 * Maximum length of the serialized HTML string stored in the database.
 * The serialized form includes HTML entities (e.g., "=" becomes "&#x3D;")
 * and content source links which are much longer than their visible text.
 */
export const MAX_SERIALIZED_HTML_LENGTH = 6000;

/**
 * Decodes HTML entities in a string to get the visible text.
 * Used to calculate the actual character count that users see.
 *
 * @example
 * decodeHtmlEntities("Hello &#x3D; World") // Returns "Hello = World"
 * // Input:  14 chars (serialized)
 * // Output: 13 chars (visible)
 */
export function decodeHtmlEntities(text: string): string {
  let parser = new DOMParser();
  let doc = parser.parseFromString(text, 'text/html');
  return doc.body.textContent ?? '';
}

/**
 * Calculates the visible character count from serialized HTML text.
 */
export function getVisibleCharacterCount(serializedText: string | null | undefined): number {
  if (!serializedText) {
    return 0;
  }
  return decodeHtmlEntities(serializedText).length;
}
